import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Gauge, Timer } from 'lucide-react';
import { useActivationParticipants, useRecordScore } from '@/hooks/useActivationParticipants';

interface BlazePodScorePanelProps {
  activationId: string;
}

/**
 * Operator score-entry surface for a BlazePod (speed-tap) activation:
 *  - lists registered guests (recent-first) and lets the operator pick one,
 *  - shows the guest's current best score (a "Done — …" prefix once scored),
 *  - records a numeric score (successful taps); keep-highest is enforced server-side,
 *  - resets the input and refreshes the list (best scores) on success.
 *
 * Mirrors OperatorCapturePanel but with a numeric score input instead of a
 * photo upload + generate.
 */
export function BlazePodScorePanel({ activationId }: BlazePodScorePanelProps) {
  const participantsQuery = useActivationParticipants(activationId);
  const recordScore = useRecordScore(activationId);

  const [selectedParticipantId, setSelectedParticipantId] = useState<string>('');
  const [scoreText, setScoreText] = useState<string>('');
  const [lastBestScore, setLastBestScore] = useState<number | null>(null);

  const participants = useMemo(
    () =>
      [...(participantsQuery.data ?? [])].sort(
        (a, b) => new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime(),
      ),
    [participantsQuery.data],
  );

  // A valid score is a non-negative integer.
  const parsedScore = Number(scoreText);
  const isValidScore =
    scoreText.trim() !== '' &&
    Number.isInteger(parsedScore) &&
    parsedScore >= 0;
  const canRecord = !!selectedParticipantId && isValidScore && !recordScore.isPending;

  const handleRecord = async () => {
    if (!canRecord) return;
    const result = await recordScore.mutateAsync({
      participantId: selectedParticipantId,
      data: { score: parsedScore },
    });
    setLastBestScore(result.bestScore);
    setScoreText('');
  };

  return (
    <Card data-testid="blazepod-score-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5" />
          Record speed-tap score
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {participantsQuery.isLoading && (
          <Skeleton className="h-24 w-full" data-testid="blazepod-participants-loading" />
        )}
        {participantsQuery.isError && (
          <Alert variant="destructive" data-testid="blazepod-participants-error">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load registered guests</AlertTitle>
            <AlertDescription>{(participantsQuery.error as Error)?.message}</AlertDescription>
          </Alert>
        )}

        {!participantsQuery.isLoading && !participantsQuery.isError && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="blazepod-participant">Registered guest</Label>
              {participants.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="blazepod-participants-empty">
                  No registered guests yet. Guests register by scanning the activation QR.
                </p>
              ) : (
                <Select value={selectedParticipantId} onValueChange={setSelectedParticipantId}>
                  <SelectTrigger id="blazepod-participant" data-testid="blazepod-participant-picker">
                    <SelectValue placeholder="Pick the guest who just played…" />
                  </SelectTrigger>
                  <SelectContent>
                    {participants.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`blazepod-participant-${p.id}`}>
                        {p.score != null ? 'Done — ' : ''}
                        {p.name || 'Unnamed guest'}
                        {p.email ? ` · ${p.email}` : ''} ·{' '}
                        {new Date(p.registeredAt).toLocaleTimeString()}
                        {p.score != null ? ` · best ${p.score}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="blazepod-score">
                <span className="flex items-center gap-1.5">
                  <Timer className="h-4 w-4" />
                  Successful taps
                </span>
              </Label>
              <Input
                id="blazepod-score"
                data-testid="blazepod-score-input"
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                placeholder="e.g. 42"
                value={scoreText}
                onChange={(e) => setScoreText(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Enter the number of successful taps. Re-scoring keeps the highest.
              </p>
            </div>

            <Button
              type="button"
              disabled={!canRecord}
              onClick={handleRecord}
              data-testid="blazepod-record-button"
            >
              <Gauge className="mr-2 h-4 w-4" />
              {recordScore.isPending ? 'Recording…' : 'Record score'}
            </Button>

            {lastBestScore != null && (
              <p className="text-sm text-muted-foreground" data-testid="blazepod-recorded-confirmation">
                Recorded. Current best score: <span className="font-medium text-foreground">{lastBestScore}</span>.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
